import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useProfile } from "@hooks";
import {
  selectNormalizedMeetingState,
  selectMeetingHosts,
  validateNormalizedMeetingState
} from "@store/selectors/meetingSelectors";

const useMeeting = () => {
  const [hostId, setHostId] = useState(null);
  const [meetingParticipants, setMeetingParticipants] = useState([]);
  const [participantsIncludingHost, setParticipantsIncludingHost] = useState([]);
  const [guests, setGuests] = useState([]);
  const { profile } = useProfile();
  const [meetingObj, setMeetingObj] = useState({});
  
  // Use normalized selectors for platform-agnostic access
  const normalizedMeeting = useSelector(selectNormalizedMeetingState);
  const meetingHosts = useSelector(selectMeetingHosts);
  const isFetchingMeeting = useSelector((state) => state.meeting.isFetchingMeeting);
  
  // Validate state in development
  useSelector(validateNormalizedMeetingState);
  
  // Extract normalized values
  const isMeetingActive = normalizedMeeting.isActive;
  const meetingInstanceId = normalizedMeeting.meetingInstanceId;
  
  // Platform-specific data extraction
  const platformMeetingInstanceId = normalizedMeeting.platform === 'ZOOM' 
    ? normalizedMeeting.platformData?.platformMeetingInstanceId 
    : normalizedMeeting.meetingInstanceId;
    
  const platformMeetingId = normalizedMeeting.platform === 'ZOOM'
    ? normalizedMeeting.platformData?.platformMeetingId
    : normalizedMeeting.meetingInstanceId;

  const isHost = useCallback((id) => {
    // Use consistent logic across both platforms via selectMeetingHosts
    return meetingHosts.host && meetingHosts.host?.userId === id;
  }, [meetingHosts.host]);

  const isCoHost = useCallback((id) => {
    // Web meetings don't have co-hosts, Zoom meetings do
    return !!(meetingHosts.coHosts.length && meetingHosts.coHosts.find((coHost) => coHost.userId === id));
  }, [meetingHosts.coHosts]);

  const isHostOrCoHost = useCallback((id) => isHost(id) || isCoHost(id), [isHost, isCoHost]);

  useEffect(() => {
    // Use normalized meeting data for platform-agnostic logic
    if (normalizedMeeting.isActive && normalizedMeeting.meetingInstanceId) {
      setHostId(normalizedMeeting.hostId);
      setMeetingParticipants(normalizedMeeting.participants);
      
      // Create normalized participants list that includes host
      const createNormalizedParticipantsList = () => {
        const participants = normalizedMeeting.participants || [];
        const host = meetingHosts.host;
        
        if (!host) {
          return participants;
        }
        
        // Check if host is already in participants list (Zoom case)
        const hostInParticipants = participants.find(p => p._id === host.userId);
        
        if (hostInParticipants) {
          // Zoom case: host is already in participants, return as-is
          return participants;
        } else {
          // Web case: host not in participants, add host to the beginning
          const hostAsParticipant = {
            _id: host.userId,
            firstName: host.firstName,
            lastName: host.lastName,
            username: host.firstName, // Use firstName as username fallback
            email: host.email,
            color: host.color,
            avatar: host.avatar,
            isHost: true // Mark as host for easy identification
          };
          
          return [hostAsParticipant, ...participants];
        }
      };
      
      setParticipantsIncludingHost(createNormalizedParticipantsList());
      
      // Set platform-specific data for backward compatibility
      if (normalizedMeeting.platform === 'ZOOM') {
        setMeetingObj(normalizedMeeting.platformData.meetingObject);
        setGuests(normalizedMeeting.platformData.participantsInMeetingOnZoom || []);
      } else if (normalizedMeeting.platform === 'WEB') {
        setMeetingObj(normalizedMeeting.platformData.webMeetingInstance);
        setGuests([]); // Web meetings don't have separate guests concept yet
      }
    } else {
      // No active meeting - reset to defaults
      setMeetingParticipants(profile ? [profile] : []);
      setParticipantsIncludingHost(profile ? [profile] : []);
      setGuests([]);
      setMeetingObj({});
      setHostId(null);
    }
  }, [normalizedMeeting, meetingHosts, profile]);

  return {
    hostId,
    isCoHost,
    isHost,
    isHostOrCoHost,
    isFetchingMeeting,
    meetingParticipants,
    participantsIncludingHost, // New: includes host + participants for consistent display
    guests,
    isMeetingActive,
    meetingObj,
    meetingInstanceId,
    platformMeetingId,
    platformMeetingInstanceId,
    // Platform-agnostic meeting instance
    meetingInstance: normalizedMeeting.platform === 'ZOOM' 
      ? meetingObj?.meetingInstances?.[0] || {} 
      : meetingObj || {},
    // New normalized fields (for components that want to use them)
    normalizedMeeting,
    platform: normalizedMeeting.platform,
    currentAgenda: normalizedMeeting.agenda
  };
};

export default useMeeting;
